import React, { useState, useEffect, useMemo } from 'react';
import { reaggregateFromCache, thumbUrl } from './metaApiSource';
import { aggregateByCorIdAndCategory } from './csvParserV2';
import { parseNomenclature } from './nomenclatureParser';
import PerfBlockHeader from './PerfBlockHeader';
import { isCreatorAd, AD_PREFIX_RE } from '../creatorType';

const kEuro = (n) => (n >= 1000 ? '€' + (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace('.0', '') + 'k' : '€' + Math.round(n || 0));
const fullEuro = (n) => '€' + Math.round(n || 0).toLocaleString('en-US');
const numFmt = (n) => Math.round(n || 0).toLocaleString('en-US');
const GRAN_LABEL = { week: 'Weekly', month: 'Monthly', quarter: 'Quarterly' };

function shortCreator(ad) {
  const raw = (parseNomenclature(ad.ad_name_sample).creator) || (ad.ad_name_sample || '').split('_')[0] || '—';
  return String(raw).replace(/_/g, ' ');
}
function isoWeekKey(dstr) {
  if (!dstr) return null; const d = new Date(dstr + 'T00:00:00Z'); if (isNaN(d)) return null;
  const dt = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = dt.getUTCDay() || 7; dt.setUTCDate(dt.getUTCDate() + 4 - day);
  const y = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  const wk = Math.ceil((((dt - y) / 86400000) + 1) / 7);
  return dt.getUTCFullYear() + '-W' + String(wk).padStart(2, '0');
}
function monthKey(d) { return d && d.length >= 7 ? d.slice(0, 7) : null; }
function quarterKey(d) { if (!d || d.length < 7) return null; return d.slice(0, 4) + '-Q' + (Math.floor((+d.slice(5, 7) - 1) / 3) + 1); }
function labelFor(gran, key) {
  if (!key) return '—';
  if (gran === 'month') { const [y, m] = key.split('-'); return new Date(Date.UTC(+y, +m - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }); }
  if (gran === 'quarter') { return key.replace('-', ' '); }
  const [y, w] = key.split('-W'); return 'Week ' + (+w) + ' ' + y;
}

export default function SpendReportModal({ open, onClose }) {
  const [rows, setRows] = useState(null);
  const [gran, setGran] = useState('month');
  const [hover, setHover] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows(null);
    let alive = true;
    (async () => {
      const until = new Date().toISOString().slice(0, 10);
      const s6 = new Date(); s6.setMonth(s6.getMonth() - 6);
      const since = s6.toISOString().slice(0, 10);
      try { const r = await reaggregateFromCache(since, until); if (alive) setRows(r || []); }
      catch (e) { if (alive) setRows([]); }
    })();
    return () => { alive = false; };
  }, [open]);

  const buckets = useMemo(() => {
    if (!rows) return [];
    const keyFn = gran === 'week' ? isoWeekKey : gran === 'quarter' ? quarterKey : monthKey;
    const map = new Map();
    for (const r of rows) {
      const k = keyFn(r.date_start || r.date_end); if (!k) continue;
      if (!map.has(k)) map.set(k, { key: k, spend: 0, purchases: 0, core: new Set(), rows: [] });
      const b = map.get(k); b.spend += r.spend || 0; b.purchases += r.results || 0;
      if (r.core_id) b.core.add(r.core_id); b.rows.push(r);
    }
    return Array.from(map.values()).sort((a, b) => (a.key < b.key ? -1 : 1));
  }, [rows, gran]);

  const latest = buckets.length ? buckets[buckets.length - 1] : null;

  const report = useMemo(() => {
    if (!latest) return null;
    const agg = aggregateByCorIdAndCategory(latest.rows);
    const infl = agg.filter(a => a.ad_owner === 'influencer' || isCreatorAd(a.ad_name_sample));
    const base = infl.length ? infl : agg;
    const top3 = [...base].sort((a, b) => (b.achats || 0) - (a.achats || 0)).slice(0, 3);
    const feat = {};
    base.forEach(a => { const f = parseNomenclature(a.ad_name_sample).feature || null; if (!f) return; if (!feat[f]) feat[f] = { spend: 0, ach: 0, n: 0 }; feat[f].spend += a.spend || 0; feat[f].ach += a.achats || 0; feat[f].n++; });
    const bestFeat = Object.entries(feat).map(([k, v]) => ({ k, ...v, cpa: v.ach ? v.spend / v.ach : null })).sort((a, b) => b.ach - a.ach)[0] || null;
    const inf = {};
    base.forEach(a => { const c = shortCreator(a); if (!inf[c]) inf[c] = { spend: 0, ach: 0, n: 0 }; inf[c].spend += a.spend || 0; inf[c].ach += a.achats || 0; inf[c].n++; });
    const bestInf = Object.entries(inf).map(([k, v]) => ({ k, ...v, cpa: v.ach ? v.spend / v.ach : null })).sort((a, b) => b.ach - a.ach)[0] || null;
    return {
      top3, bestFeat, bestInf,
      spend: latest.spend, purchases: latest.purchases,
      cpa: latest.purchases ? latest.spend / latest.purchases : null,
      uniq: latest.core.size,
      periodLabel: labelFor(gran, latest.key),
    };
  }, [latest, gran]);

  function exportCSV() {
    const header = 'Period,Spend (EUR),Purchases,Avg CPA (EUR),Unique ads\n';
    const lines = buckets.map(b => {
      const cpa = b.purchases ? (b.spend / b.purchases).toFixed(2) : '';
      return [labelFor(gran, b.key), Math.round(b.spend), b.purchases, cpa, b.core.size].join(',');
    });
    const blob = new Blob([header + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'timbo-influencer-spend-' + gran + '.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }

  function exportPDF() {
    if (!report) return;
    const stat = (l, v) => `<div style="border:1px solid #e6e6e6;border-radius:10px;padding:12px 14px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:.5px;color:#8a8a8a;font-weight:700">${l}</div><div style="font-size:20px;font-weight:800;margin-top:4px">${v}</div></div>`;
    const row = (label, val, sub) => `<div style="border:1px solid #e6e6e6;border-radius:10px;padding:12px 14px"><div style="font-size:10px;text-transform:uppercase;color:#8a8a8a;font-weight:700">${label}</div><div style="font-size:16px;font-weight:800;margin-top:2px">${val}</div><div style="font-size:11px;color:#8a8a8a;margin-top:2px">${sub}</div></div>`;
    const top = report.top3.map((a, i) => `<tr><td style="padding:6px 8px;font-weight:700">#${i + 1}</td><td style="padding:6px 8px">${(a.ad_name_sample || '').replace(AD_PREFIX_RE, '').slice(0, 40)}</td><td style="padding:6px 8px;text-align:right">${a.achats || 0} purch.</td><td style="padding:6px 8px;text-align:right">€${a.cpa ? a.cpa.toFixed(1) : '—'} CPA</td></tr>`).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Timbo influencer report — ${report.periodLabel}</title></head>
      <body style="font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#0a0a0a;max-width:720px;margin:24px auto;padding:0 16px">
      <div style="font-weight:800;font-size:16px">timbo · Influencer report</div>
      <div style="font-size:22px;font-weight:800;margin-top:12px"><span style="color:#FF7900">${fullEuro(report.spend)}</span> spent on influencer ads in ${report.periodLabel}</div>
      <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:16px 0">
        ${stat('Spend', fullEuro(report.spend))}${stat('Purchases', numFmt(report.purchases))}${stat('Avg CPA', report.cpa != null ? '€' + report.cpa.toFixed(2) : '—')}${stat('Unique ads', numFmt(report.uniq))}
      </div>
      <div style="font-size:12px;text-transform:uppercase;color:#b0b0b0;font-weight:700;margin:8px 0">Top 3 most promising</div>
      <table style="width:100%;border-collapse:collapse;font-size:13px;border:1px solid #eee;border-radius:8px">${top}</table>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin:16px 0">
        ${row('Best feature', report.bestFeat ? report.bestFeat.k : '—', report.bestFeat ? ('€' + (report.bestFeat.cpa ? report.bestFeat.cpa.toFixed(1) : '—') + ' CPA · ' + report.bestFeat.n + ' ads · ' + report.bestFeat.ach + ' purchases') : '')}
        ${row('Best influencer', report.bestInf ? report.bestInf.k : '—', report.bestInf ? (report.bestInf.ach + ' purchases · €' + (report.bestInf.cpa ? report.bestInf.cpa.toFixed(1) : '—') + ' CPA · ' + report.bestInf.n + ' ads') : '')}
      </div>
      <div style="font-size:11px;color:#b0b0b0;margin-top:16px;border-top:1px solid #eee;padding-top:10px">Generated by Timbo · figures from Meta Ads (fb_mobile_purchase). Concept-level insights pending internal nomenclature.</div>
      <script>window.onload=function(){window.print();}</script></body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  }

  if (!open) return null;

  const W = 940, H = 150, PADL = 8, PADR = 16, PADT = 12, PADB = 22;
  const vals = buckets.map(b => b.spend);
  const max = vals.length ? Math.max(...vals) * 1.14 : 1;
  const iw = W - PADL - PADR, ih = H - PADT - PADB;
  const X = i => PADL + (buckets.length <= 1 ? iw / 2 : (i / (buckets.length - 1)) * iw);
  const Y = v => PADT + ih - (v / max) * ih;
  const pts = buckets.map((b, i) => ({ ...b, px: X(i), py: Y(b.spend) }));
  const linePath = pts.map((p, i) => (i ? 'L' : 'M') + p.px.toFixed(1) + ' ' + p.py.toFixed(1)).join(' ');
  const areaPath = pts.length ? linePath + ` L ${pts[pts.length - 1].px.toFixed(1)} ${Y(0)} L ${pts[0].px.toFixed(1)} ${Y(0)} Z` : '';
  const grid = [];
  for (let t = 0; t <= 4; t++) { const gv = max * t / 4; const gy = Y(gv); grid.push({ gy, gv }); }
  const total = vals.reduce((a, b) => a + b, 0);

  function onMove(e) {
    if (!pts.length) return;
    const r = e.currentTarget.getBoundingClientRect();
    const mx = (e.clientX - r.left) / r.width * W;
    let best = pts[0], bd = 1e9;
    for (const p of pts) { const d = Math.abs(p.px - mx); if (d < bd) { bd = d; best = p; } }
    setHover(best);
  }

  const ddBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #e5e5e5', background: '#fff', color: '#333', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
  const ddMenu = { position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 6, background: '#fff', border: '1px solid #e5e5e5', borderRadius: 9, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 4, minWidth: 150 };
  const ddItem = (active) => ({ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: active ? 'rgba(255,121,0,0.08)' : 'transparent', color: active ? '#FF7900' : '#333', borderRadius: 6, padding: '8px 10px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' });
  const statCard = (l, v) => (
    <div style={{ border: '1px solid #e6e6e6', borderRadius: 9, padding: '9px 11px' }}>
      <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '.5px', color: '#FF7900', fontWeight: 700 }}>{l}</div>
      <div style={{ fontSize: 17, fontWeight: 800, marginTop: 3 }}>{v}</div>
    </div>
  );
  const secLabel = { fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.5px', color: '#b0b0b0', fontWeight: 700, marginBottom: 8 };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 1180, maxWidth: '97vw', maxHeight: '94vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <PerfBlockHeader
          variant="modal"
          title="Influencer spend & report"
          onClose={onClose}
          actions={<>
            <div style={{ position: 'relative' }}>
              <button style={ddBtn} onClick={() => { setDateOpen(o => !o); setExportOpen(false); }}>📅 {GRAN_LABEL[gran]} <span style={{ color: '#bbb', fontSize: 10 }}>▾</span></button>
              {dateOpen && (<>
                <div onClick={() => setDateOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 5 }} />
                <div style={ddMenu}>
                  {['week', 'month', 'quarter'].map(g => (
                    <button key={g} style={ddItem(gran === g)} onClick={() => { setGran(g); setDateOpen(false); }}>{GRAN_LABEL[g]}</button>
                  ))}
                </div>
              </>)}
            </div>
            <div style={{ position: 'relative' }}>
              <button style={{ ...ddBtn, background: '#111', color: '#fff', border: 'none' }} onClick={() => { setExportOpen(o => !o); setDateOpen(false); }}>⤓ Export data <span style={{ color: '#aaa', fontSize: 10 }}>▾</span></button>
              {exportOpen && (<>
                <div onClick={() => setExportOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 5 }} />
                <div style={ddMenu}>
                  <button style={ddItem(false)} onClick={() => { exportPDF(); setExportOpen(false); }}>⤓ Export as PDF</button>
                  <button style={ddItem(false)} onClick={() => { exportCSV(); setExportOpen(false); }}>⤓ Export as CSV</button>
                </div>
              </>)}
            </div>
          </>}
        />

        <div style={{ padding: '16px 20px 20px' }}>
          {rows === null ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading…</div>
          ) : buckets.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#888', fontSize: 13 }}>No cached spend data. Sync a wider range (e.g. "Last 6 months") first.</div>
          ) : (
            <>
              <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.4px' }}><span style={{ color: '#FF7900' }}>{kEuro(total)}</span> total · last 6 months <span style={{ color: '#b0b0b0', fontSize: 12, fontWeight: 600 }}>· {GRAN_LABEL[gran].toLowerCase()} view</span></div>

              <div style={{ position: 'relative', marginTop: 8 }}>
                <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 150, overflow: 'visible' }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
                  <defs><linearGradient id="spgrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#FF7900" stopOpacity="0.16" /><stop offset="1" stopColor="#FF7900" stopOpacity="0" /></linearGradient></defs>
                  {grid.map((g, i) => (<g key={i}><line x1={PADL} y1={g.gy} x2={W - PADR} y2={g.gy} stroke="#eee" strokeWidth="1" /><text x={W - PADR} y={g.gy - 3} textAnchor="end" fontSize="10" fill="#bbb">{kEuro(Math.round(g.gv))}</text></g>))}
                  {areaPath && <path d={areaPath} fill="url(#spgrad)" />}
                  <path d={linePath} fill="none" stroke="#FF7900" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  {pts.map((p, i) => { const every = buckets.length > 10 ? Math.ceil(buckets.length / 8) : 1; if (i % every !== 0 && i !== pts.length - 1) return null; return <text key={i} x={p.px} y={H - 6} textAnchor="middle" fontSize="10" fill="#aaa">{labelFor(gran, p.key).replace(' 2026', '').replace('Week ', 'W')}</text>; })}
                  {pts.length > 0 && <circle cx={pts[pts.length - 1].px} cy={pts[pts.length - 1].py} r="3.5" fill="#FF7900" />}
                  {hover && <><line x1={hover.px} y1={PADT} x2={hover.px} y2={H - PADB} stroke="#FF7900" strokeWidth="1" strokeDasharray="3 3" opacity="0.5" /><circle cx={hover.px} cy={hover.py} r="4.5" fill="#fff" stroke="#FF7900" strokeWidth="2" /></>}
                </svg>
                {hover && <div style={{ position: 'absolute', left: (hover.px / W * 100) + '%', top: (hover.py / H * 100) + '%', transform: 'translate(-50%,-140%)', background: '#111', color: '#fff', fontSize: 11, padding: '5px 8px', borderRadius: 6, pointerEvents: 'none', whiteSpace: 'nowrap' }}><b>{kEuro(hover.spend)}</b> · {labelFor(gran, hover.key)}</div>}
              </div>

              {report && (
                <div style={{ display: 'flex', gap: 20, marginTop: 14, alignItems: 'flex-start', borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                  {/* stats */}
                  <div style={{ flex: '0 0 226px' }}>
                    <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 10 }}><span style={{ color: '#FF7900' }}>{fullEuro(report.spend)}</span> in {report.periodLabel}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {statCard('Spend', fullEuro(report.spend))}
                      {statCard('Purchases', numFmt(report.purchases))}
                      {statCard('Avg CPA', report.cpa != null ? '€' + report.cpa.toFixed(2) : '—')}
                      {statCard('Unique ads', numFmt(report.uniq))}
                    </div>
                  </div>
                  {/* top 3 vertical */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={secLabel}>Top 3 most promising</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                      {report.top3.map((a, i) => { const t = thumbUrl(a); return (
                        <div key={a.core_id || i} style={{ border: '1px solid #e6e6e6', borderRadius: 10, overflow: 'hidden' }}>
                          <div style={{ aspectRatio: '9 / 16', background: '#111', position: 'relative' }}>{t && <img src={t} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}<span style={{ position: 'absolute', top: 6, left: 6, background: '#FF7900', color: '#fff', fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 10 }}>#{i + 1}</span></div>
                          <div style={{ padding: '7px 9px' }}><div style={{ fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{shortCreator(a)}</div><div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10.5, color: '#8a8a8a' }}><span><b style={{ color: '#0a0a0a' }}>{a.achats || 0}</b> purch.</span><span><b style={{ color: '#0a0a0a' }}>€{a.cpa ? a.cpa.toFixed(1) : '—'}</b> CPA</span></div></div>
                        </div>
                      ); })}
                    </div>
                  </div>
                  {/* best */}
                  <div style={{ flex: '0 0 250px' }}>
                    <div style={secLabel}>What worked best</div>
                    <div style={{ border: '1px solid #e6e6e6', borderRadius: 10, padding: '11px 13px' }}>
                      <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#FF7900', fontWeight: 700 }}>Best feature</div>
                      <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>{report.bestFeat ? report.bestFeat.k : '—'}</div>
                      <div style={{ fontSize: 11.5, color: '#8a8a8a', marginTop: 2 }}>{report.bestFeat ? ('€' + (report.bestFeat.cpa ? report.bestFeat.cpa.toFixed(1) : '—') + ' CPA · ' + report.bestFeat.n + ' ads · ' + report.bestFeat.ach + ' purch.') : 'no tagged feature'}</div>
                    </div>
                    <div style={{ border: '1px solid #e6e6e6', borderRadius: 10, padding: '11px 13px', marginTop: 10 }}>
                      <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#FF7900', fontWeight: 700 }}>Best influencer</div>
                      <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>{report.bestInf ? report.bestInf.k : '—'}</div>
                      <div style={{ fontSize: 11.5, color: '#8a8a8a', marginTop: 2 }}>{report.bestInf ? (report.bestInf.ach + ' purch. · €' + (report.bestInf.cpa ? report.bestInf.cpa.toFixed(1) : '—') + ' CPA · ' + report.bestInf.n + ' ads') : '—'}</div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
